// src/components/common/NavbarUserMenu.tsx
import React from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { Dashboard, Logout } from '@mui/icons-material';
import { UserRole } from '../../types';

interface NavbarUserMenuProps {
  user: { username: string; email?: string; role: string };
  anchorEl: null | HTMLElement;
  isRTL: boolean;
  t: (key: string) => string;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onMenuClose: () => void;
  onDashboard: () => void;
  onLogout: () => void;
}

const NavbarUserMenu: React.FC<NavbarUserMenuProps> = ({
  user,
  anchorEl,
  isRTL,
  t,
  onMenuOpen,
  onMenuClose,
  onDashboard,
  onLogout,
}) => {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Button
        onClick={onDashboard}
        sx={{
          color: 'rgb(75, 85, 99)',
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          fontFamily: 'Cairo, Arabic, sans-serif',
          transition: 'color 0.3s ease-out',
          '&:hover': {
            color: '#6523d1',
            bgcolor: 'transparent',
          },
        }}
      >
        {t('nav.dashboard')}
      </Button>

      <Button
        onClick={onMenuOpen}
        sx={{
          color: 'rgb(17, 24, 39)',
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          fontFamily: 'Cairo, Arabic, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: '9999px',
          bgcolor: 'rgba(101, 35, 209, 0.05)',
          border: '1px solid rgba(101, 35, 209, 0.2)',
          transition: 'all 0.3s ease-out',
          '&:hover': {
            bgcolor: 'rgba(101, 35, 209, 0.1)',
            borderColor: 'rgba(101, 35, 209, 0.3)',
          },
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: '#6523d1',
            fontSize: '0.875rem',
            fontWeight: 600,
            fontFamily: 'Cairo, Arabic, sans-serif',
          }}
        >
          {user.username.charAt(0).toUpperCase()}
        </Avatar>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, fontFamily: 'Cairo, Arabic, sans-serif' }}
        >
          {user.username}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          '& .MuiPaper-root': {
            bgcolor: 'white',
            border: '1px solid rgba(209, 213, 219, 0.3)',
            borderRadius: 3,
            mt: 1,
            minWidth: 200,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          },
        }}
      >
        <MenuItem
          disabled
          sx={{
            opacity: 1,
            cursor: 'default',
            fontFamily: 'Cairo, Arabic, sans-serif',
            '&.Mui-disabled': { opacity: 1 },
          }}
        >
          <Box sx={{ textAlign: 'right', width: '100%' }}>
            <Typography
              variant="body2"
              sx={{
                color: 'rgb(107, 114, 128)',
                fontSize: '0.75rem',
                fontFamily: 'Cairo, Arabic, sans-serif',
              }}
            >
              {user.email || user.username}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#6523d1',
                fontSize: '0.7rem',
                fontWeight: 500,
                fontFamily: 'Cairo, Arabic, sans-serif',
              }}
            >
              {user.role === UserRole.ADMIN ? t('nav.systemAdmin') : t('nav.restaurantOwner')}
            </Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ borderColor: 'rgba(209, 213, 219, 0.3)', my: 1 }} />
        <MenuItem
          onClick={onDashboard}
          sx={{
            color: 'rgb(17, 24, 39)',
            fontFamily: 'Cairo, Arabic, sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1.5,
            '&:hover': { bgcolor: 'rgba(101, 35, 209, 0.05)' },
          }}
        >
          <Dashboard sx={{ fontSize: 20, [isRTL ? 'ml' : 'mr']: 1 }} />
          {t('nav.dashboard')}
        </MenuItem>
        <MenuItem
          onClick={onLogout}
          sx={{
            color: 'rgb(239, 68, 68)',
            fontFamily: 'Cairo, Arabic, sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1.5,
            '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.05)' },
          }}
        >
          <Logout sx={{ fontSize: 20, [isRTL ? 'ml' : 'mr']: 1 }} />
          {t('nav.logout')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NavbarUserMenu;
